import { useCallback, useEffect, useState } from "react";
import { words } from "../data/words";
import {
  getRandomWord,
  submitHint as submitHintToApi,
  updateGuessResult
} from "../utils/api";
import {
  getContributorId,
  getSeenWords,
  getStats,
  saveSeenWords,
  saveStats
} from "../utils/storage";

const sampleHints = (hints, count) =>
  [...hints]
    .sort(() => Math.random() - 0.5)
    .slice(0, count)
    .sort((a, b) => a.weight - b.weight);

const localFallbackWord = () => {
  const word = words[Math.floor(Math.random() * words.length)];
  return {
    ...word,
    id: null,
    hints: word.hints.map((hint, index) => ({ ...hint, id: `local-${index}` }))
  };
};

export default function Home() {
  const [currentWord, setCurrentWord] = useState(null);
  const [hintStack, setHintStack] = useState([]);
  const [index, setIndex] = useState(0);
  const [shown, setShown] = useState([]);
  const [guess, setGuess] = useState("");
  const [showPost, setShowPost] = useState(false);
  const [betterHint, setBetterHint] = useState("");
  const [resultFlash, setResultFlash] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const startRound = useCallback((word) => {
    const sorted = sampleHints(word.hints, 3);
    const seen = getSeenWords();
    const seenId = word.id || word.word;

    if (!seen.includes(seenId)) {
      saveSeenWords([...seen, seenId]);
    }

    setCurrentWord(word);
    setHintStack(sorted);
    setIndex(0);
    setShown([sorted[0]]);
    setShowPost(false);
    setGuess("");
    setBetterHint("");
    setResultFlash(null);
  }, []);

  const startGame = useCallback(async () => {
    setLoading(true);
    setStatus("");

    try {
      const word = await getRandomWord();
      startRound(word);
    } catch {
      setStatus("Using local words until the database API is reachable.");
      startRound(localFallbackWord());
    } finally {
      setLoading(false);
    }
  }, [startRound]);

  useEffect(() => {
    startGame();
  }, [startGame]);

  function nextHint() {
    if (index < hintStack.length - 1) {
      const newIndex = index + 1;
      setIndex(newIndex);
      setShown([...shown, hintStack[newIndex]]);
    }
  }

  async function submitGuess() {
    if (!currentWord || resultFlash) return;

    const stats = getStats();
    const activeHint = shown[shown.length - 1];
    const previousHints = shown.slice(0, -1);
    const isCorrect =
      guess.trim().toLowerCase() === currentWord.word.toLowerCase();

    if (currentWord.id && activeHint.id) {
      try {
        await updateGuessResult({
          wordId: currentWord.id,
          activeHintId: activeHint.id,
          previousHintIds: previousHints.map(hint => hint.id).filter(Boolean),
          correct: isCorrect,
          contributorId: getContributorId()
        });
      } catch {
        setStatus("Could not update the shared hit ratio. Please try again.");
      }
    }

    if (isCorrect) {
      stats.correctGuesses++;
      saveStats(stats);
    }

    setResultFlash(isCorrect ? "success" : "fail");

    setTimeout(() => {
      setResultFlash(null);
      setShowPost(true);
    }, 1000);
  }

  async function submitHint() {
    const text = betterHint.trim();
    if (!text || !currentWord?.id) return;

    try {
      await submitHintToApi({
        wordId: currentWord.id,
        text,
        contributorId: getContributorId()
      });
      setBetterHint("");
      setStatus("Hint submitted for approval.");
    } catch {
      setStatus("Could not submit hint. Please try again.");
    }
  }

  if (loading && !currentWord) {
    return <div className="card">Loading...</div>;
  }

  if (!currentWord || shown.length === 0) return null;

  return (
    <div className="card">
      {resultFlash && (
        <div className={`result-flash ${resultFlash}`}>
          <div className="result-symbol">
            {resultFlash === "success" ? "✓" : "×"}
          </div>
          <div className="result-text">
            {resultFlash === "success" ? "Yayy" : "Oops"}
          </div>
        </div>
      )}

      <div className="hint">{shown[shown.length - 1].text}</div>

      <button onClick={nextHint} disabled={index >= hintStack.length - 1}>
        Shuffle Hint
      </button>

      <input
        value={guess}
        onChange={e => setGuess(e.target.value)}
        placeholder="Guess..."
      />

      <button className="secondary" onClick={submitGuess}>
        Submit Guess
      </button>

      {showPost && (
        <>
          <input
            placeholder="Give a better hint..."
            value={betterHint}
            onChange={e => setBetterHint(e.target.value)}
          />

          <button onClick={submitHint} disabled={!currentWord.id}>
            Submit Hint
          </button>

          <button className="secondary" onClick={startGame}>
            Next Word ->
          </button>
        </>
      )}

      {status && <div className="status-text">{status}</div>}
    </div>
  );
}
